import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import {
  VisibilityOutlined as Visibility,
  VisibilityOffOutlined as VisibilityOff,
} from "@mui/icons-material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { changePasswordSchema } from "../../schemas/Auth.schema";
import { changePassword } from "../../services/userService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { t } = useAppTranslation();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setCurrentBreadcrumbPage(t("profile.changePassword.breadcrumb"));
  }, [setCurrentBreadcrumbPage, t]);

  const formik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { resetForm }) => {
      if (isSaving) return;
      try {
        setIsSaving(true);
        const response = await changePassword(values);
        resetForm();
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        showToast(
          response?.message || t("profile.changePassword.toast.success"),
          "success",
        );
      } catch (error: any) {
        showToast(
          extractApiErrorMessage(
            error,
            t("profile.changePassword.toast.failed"),
          ),
          "error",
        );
      } finally {
        setIsSaving(false);
      }
    },
  });

  const renderVisibilityAdornment = (
    isVisible: boolean,
    toggle: () => void,
    ariaLabel: string,
  ) => (
    <InputAdornment position="end">
      <IconButton onClick={toggle} edge="end" aria-label={ariaLabel}>
        {isVisible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box>
      <Loader isLoading={isSaving} />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Box className="text-box-card-white m-0">
          <Box className="text-box-header">
            <h3>{t("profile.changePassword.header")}</h3>
          </Box>

          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <Stack sx={{ marginBottom: "20px" }}>
                <label className="label-text" htmlFor="current_password">
                  {t("profile.changePassword.label.currentPassword")}
                  <span className="required-asterisk">*</span>
                </label>
                <TextField
                  id="current_password"
                  name="current_password"
                  type={showCurrentPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder={t(
                    "profile.changePassword.placeholder.currentPassword",
                  )}
                  className={`form-control withAdornment ${
                    formik.touched.current_password &&
                    formik.errors.current_password
                      ? "has-error"
                      : ""
                  }`}
                  value={formik.values.current_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    !!(
                      formik.touched.current_password &&
                      formik.errors.current_password
                    )
                  }
                  helperText={
                    formik.touched.current_password &&
                    formik.errors.current_password
                  }
                  InputProps={{
                    endAdornment: renderVisibilityAdornment(
                      showCurrentPassword,
                      () => setShowCurrentPassword((prev) => !prev),
                      showCurrentPassword
                        ? t("profile.changePassword.aria.hideCurrentPassword")
                        : t("profile.changePassword.aria.showCurrentPassword"),
                    ),
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <Stack sx={{ marginBottom: "20px" }}>
                <label className="label-text" htmlFor="new_password">
                  {t("profile.changePassword.label.newPassword")}
                  <span className="required-asterisk">*</span>
                </label>
                <TextField
                  id="new_password"
                  name="new_password"
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={t(
                    "profile.changePassword.placeholder.newPassword",
                  )}
                  className={`form-control withAdornment ${
                    formik.touched.new_password && formik.errors.new_password
                      ? "has-error"
                      : ""
                  }`}
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    !!(
                      formik.touched.new_password && formik.errors.new_password
                    )
                  }
                  helperText={
                    formik.touched.new_password && formik.errors.new_password
                  }
                  InputProps={{
                    endAdornment: renderVisibilityAdornment(
                      showNewPassword,
                      () => setShowNewPassword((prev) => !prev),
                      showNewPassword
                        ? t("profile.changePassword.aria.hideNewPassword")
                        : t("profile.changePassword.aria.showNewPassword"),
                    ),
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <Stack sx={{ marginBottom: "20px" }}>
                <label className="label-text" htmlFor="confirm_password">
                  {t("profile.changePassword.label.confirmPassword")}
                  <span className="required-asterisk">*</span>
                </label>
                <TextField
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={t(
                    "profile.changePassword.placeholder.confirmPassword",
                  )}
                  className={`form-control withAdornment ${
                    formik.touched.confirm_password &&
                    formik.errors.confirm_password
                      ? "has-error"
                      : ""
                  }`}
                  value={formik.values.confirm_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    !!(
                      formik.touched.confirm_password &&
                      formik.errors.confirm_password
                    )
                  }
                  helperText={
                    formik.touched.confirm_password &&
                    formik.errors.confirm_password
                  }
                  InputProps={{
                    endAdornment: renderVisibilityAdornment(
                      showConfirmPassword,
                      () => setShowConfirmPassword((prev) => !prev),
                      showConfirmPassword
                        ? t("profile.changePassword.aria.hideConfirmPassword")
                        : t("profile.changePassword.aria.showConfirmPassword"),
                    ),
                  }}
                />
              </Stack>
            </Grid>
          </Grid>

          <Box>
            <FlexBetween
              sx={{
                marginTop: "16px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                className="btn-orange"
                disabled={isSaving}
              >
                <SaveOutlinedIcon /> &nbsp;{" "}
                {isSaving
                  ? t("profile.changePassword.button.saving")
                  : t("profile.changePassword.button.save")}
              </Button>
              <Button
                type="button"
                color="secondary"
                variant="contained"
                className="btn-orange btn-dark"
                onClick={() => navigate("/profile")}
                disabled={isSaving}
              >
                {t("profile.button.cancel")}
              </Button>
            </FlexBetween>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default ChangePassword;
